import i18n from 'i18next';
import { configs, displayNameTypes, formats, imperialLocales, kilometersInMile, unitTypes } from './constants';
import { createIntl, FormatDisplayNameOptions } from '@formatjs/intl';
import { selectUnit } from '@formatjs/intl-utils';
import { TFunction } from 'i18next';
import { intl } from './intl';

export const isImperialLocale = () => {
  return imperialLocales.includes(i18n.language);
};

export function convertMetersToKilometers(value: number): number {
  return value / 1000;
}

export function convertMilesToKilometers(value: number): number {
  return value * kilometersInMile;
}

export function convertKilometersToMiles(value: number): number {
  return value / kilometersInMile;
}

export function formatDate(input: any, format: string): string {
  switch (format) {
    case formats.dateCustom:
      // For some reason when 'day' displaying is not configured, the date format breaks
      if (input.config.year && input.config.month && !input.config.day) {
        const parts = intl.formatDateToParts(new Date(input.value), { ...input.config, day: 'numeric' });
        let literalRemoved = false;
        return parts
          .filter((part, index) => {
            const isClosestDayLiteral =
              !literalRemoved &&
              ((part.type === 'literal' && parts[index + 1].type === 'day') ||
                (part.type === 'literal' && parts[index - 1].type === 'day'));
            if (isClosestDayLiteral) {
              literalRemoved = true;
            }
            return !(part.type === 'day' || isClosestDayLiteral);
          })
          .map(part => part.value)
          .join('');
      }
      return intl.formatDate(new Date(input.value), {
        ...input.config
      });
    case formats.dateFull:
      return intl.formatDate(new Date(input), configs.dateFull);
    case formats.dateLong:
      return intl.formatDate(new Date(input), configs.dateLong);
    case formats.dateMedium:
      return intl.formatDate(new Date(input), configs.dateMedium);
    case formats.dateShort:
      return intl.formatDate(new Date(input), configs.dateShort);
    default:
      return intl.formatDate(new Date(input));
  }
}

export function formatDistance(value: string): string {
  const distance = Number(value);
  const unit = isImperialLocale() ? unitTypes.mile : unitTypes.kilometer;

  return intl.formatNumber(distance, {
    ...configs.unit,
    unit
  });
}

export function formatTime(value: any, format: string): string {
  const partsToTimeStrings = (parts: Intl.DateTimeFormatPart[]) => {
    const time = parts
      .filter(part => ['hour', 'minute', 'second'].includes(part.type))
      .map(part => part.value)
      .join(':');
    const dayPeriodIndex = parts.findIndex(part => part.type === 'dayPeriod');
    return dayPeriodIndex !== -1 ? `${time} ${parts[dayPeriodIndex].value}` : time;
  };
  switch (format) {
    case formats.timeShort:
      return partsToTimeStrings(
        intl.formatTimeToParts(value.time, {
          timeZoneName: value.timeZoneId && configs.timeShort.timeZoneName,
          timeZone: value.timeZoneId
        })
      );
    case formats.timeMedium:
      return partsToTimeStrings(
        intl.formatTimeToParts(value.time, {
          ...configs.timeMedium
        })
      );
    default:
      return partsToTimeStrings(intl.formatTimeToParts(new Date(value)));
  }
}

export function formatNumber(input: any): string {
  if (input.config) {
    return intl.formatNumber(Number(input.value), {
      ...configs.unit,
      ...input.config
    });
  }

  return intl.formatNumber(Number(input));
}

export function formatOrdinalNumber(value: string | number, t: TFunction): string {
  const ordinalKey = intl.formatPlural(Number(value), {
    type: 'ordinal'
  });
  const getOrdinalNumber = () => {
    switch (ordinalKey) {
      case 'one':
        return t('formats.ordinalNumber.one', {
          value,
          defaultValue: '{{value}}st'
        });
      case 'two':
        return t('formats.ordinalNumber.two', {
          value,
          defaultValue: '{{value}}nd'
        });
      case 'few':
        return t('formats.ordinalNumber.few', {
          value,
          defaultValue: '{{value}}rd'
        });
      case 'other':
        return t('formats.ordinalNumber.other', {
          value,
          defaultValue: '{{value}}th'
        });
      case 'many':
        return t('formats.ordinalNumber.many', {
          value,
          defaultValue: '{{value}}'
        });
      default:
        return value.toString();
    }
  };
  return getOrdinalNumber() || value.toString();
}

export function formatCurrency(value: string): string {
  return intl.formatNumber(Number(value), configs.currency);
}

export function formatRelative(date: string | Date, format: string): string {
  const { value, unit } = selectUnit(new Date(date).valueOf());
  switch (format) {
    case formats.relativeNarrow:
      return intl.formatRelativeTime(value, unit, configs.relativeNarrow);
    case formats.relativeShort:
      return intl.formatRelativeTime(value, unit, configs.relativeShort);
    case formats.relativeLong:
    default:
      return intl.formatRelativeTime(value, unit, configs.relativeLong);
  }
}

export function formatDisplayName(
  value: string,
  options: FormatDisplayNameOptions & { customLocale?: string } = { type: displayNameTypes.language }
) {
  if (options.customLocale) {
    const customIntl = createIntl({
      locale: options.customLocale,
      messages: {}
    });

    return customIntl.formatDisplayName(value, options);
  }
  return intl.formatDisplayName(value, options);
}

export function getBrowserLocale() {
  const browserLocale = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;

  const trimmedLocale = browserLocale.trim();
  const splitLocale = trimmedLocale.split(/-|_/);
  return { language: splitLocale[0], country: splitLocale[1] };
}
